import { ReactNode, useState } from "react";

type StudioLayoutProps = {
  sidebar: ReactNode;
  main: ReactNode;
  details?: ReactNode;
};

export function StudioLayout({ sidebar, main, details }: StudioLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <>
      <style>{`
        .studio-layout {
          display: flex;
          flex-direction: column;
          height: 100vh;
          height: 100dvh;
          overflow: hidden;
          background-color: #0a0a0a;
        }

        .studio-mobile-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          background-color: #0a0a0a;
          border-bottom: 1px solid #333;
          flex-shrink: 0;
        }

        .studio-mobile-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: #1a1a1a;
          border: 1px solid #333;
          border-radius: 8px;
          color: #e0e0e0;
          cursor: pointer;
          transition: background-color 0.15s ease;
        }

        .studio-mobile-btn:hover {
          background: #2a2a2a;
        }

        .studio-mobile-btn.active {
          background: #4a90d9;
          border-color: #4a90d9;
        }

        .studio-content-wrapper {
          display: flex;
          flex: 1;
          min-height: 0;
          position: relative;
        }

        .studio-sidebar {
          position: fixed;
          top: 0;
          left: 0;
          width: 85%;
          max-width: 320px;
          height: 100%;
          background-color: #0a0a0a;
          border-right: 1px solid #333;
          overflow: auto;
          padding: 1rem;
          z-index: 50;
          transform: translateX(-100%);
          transition: transform 0.3s ease;
        }

        .studio-sidebar.open {
          transform: translateX(0);
        }

        .studio-main {
          flex: 1;
          overflow: auto;
          padding: 1rem;
          min-width: 0;
        }

        .studio-details {
          position: fixed;
          top: 0;
          right: 0;
          width: 85%;
          max-width: 400px;
          height: 100%;
          background-color: #0a0a0a;
          border-left: 1px solid #333;
          overflow: auto;
          padding: 1rem;
          z-index: 50;
          transform: translateX(100%);
          transition: transform 0.3s ease;
        }

        .studio-details.open {
          transform: translateX(0);
        }

        .studio-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.6);
          z-index: 40;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
        }

        .studio-overlay.visible {
          opacity: 1;
          pointer-events: auto;
        }

        .studio-panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-bottom: 1rem;
          margin-bottom: 1rem;
          border-bottom: 1px solid #333;
        }

        .studio-panel-title {
          font-size: 1rem;
          font-weight: 600;
          color: #e0e0e0;
          margin: 0;
        }

        .studio-close-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: transparent;
          border: none;
          color: #888;
          cursor: pointer;
          border-radius: 4px;
        }

        .studio-close-btn:hover {
          background: #333;
          color: #e0e0e0;
        }

        @media (min-width: 768px) {
          .studio-mobile-header {
            display: none;
          }

          .studio-layout {
            flex-direction: row;
          }

          .studio-content-wrapper {
            flex-direction: row;
          }

          .studio-sidebar {
            position: relative;
            transform: none;
            width: 280px;
            flex-shrink: 0;
            z-index: auto;
          }

          .studio-details {
            position: relative;
            transform: none;
            width: 360px;
            flex-shrink: 0;
            z-index: auto;
          }

          .studio-overlay {
            display: none;
          }

          .studio-panel-header {
            display: none;
          }
        }

        @media (min-width: 1024px) {
          .studio-sidebar {
            width: 300px;
          }

          .studio-details {
            width: 400px;
          }
        }
      `}</style>

      <div className="studio-layout">
        {/* Mobile Header */}
        <div className="studio-mobile-header">
          <button
            className={`studio-mobile-btn ${sidebarOpen ? 'active' : ''}`}
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle sidebar"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>

          <span style={{ color: '#e0e0e0', fontSize: '0.875rem', fontWeight: 500 }}>
            Studio
          </span>

          {details ? (
            <button
              className={`studio-mobile-btn ${detailsOpen ? 'active' : ''}`}
              onClick={() => setDetailsOpen(!detailsOpen)}
              aria-label="Toggle details"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </button>
          ) : (
            <div style={{ width: 40 }} />
          )}
        </div>

        {/* Overlay for mobile panels */}
        <div
          className={`studio-overlay ${sidebarOpen || detailsOpen ? 'visible' : ''}`}
          onClick={() => {
            setSidebarOpen(false);
            setDetailsOpen(false);
          }}
        />

        <div className="studio-content-wrapper">
          {/* Sidebar */}
          <div className={`studio-sidebar ${sidebarOpen ? 'open' : ''}`}>
            <div className="studio-panel-header">
              <h2 className="studio-panel-title">Jobs</h2>
              <button
                className="studio-close-btn"
                onClick={() => setSidebarOpen(false)}
                aria-label="Close sidebar"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            {sidebar}
          </div>

          {/* Main content */}
          <div className="studio-main">
            {main}
          </div>

          {/* Details panel (optional) */}
          {details && (
            <div className={`studio-details ${detailsOpen ? 'open' : ''}`}>
              <div className="studio-panel-header">
                <h2 className="studio-panel-title">Details</h2>
                <button
                  className="studio-close-btn"
                  onClick={() => setDetailsOpen(false)}
                  aria-label="Close details"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              {details}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
